import { useCallback, useEffect, useRef, useState } from "react";

import { Agent, AgentsSortOptionEnum } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";

// 追加読み込み1回あたりの取得件数。1画面に収める上限ではなく、末尾まで見たら次を足す単位として使う。
//
// 20 にしている理由は2つ。
// 1. サーバは返した件数ぶんの AgentImpressionLog を記録する。per_page を大きくすると、
//    スクロールされていない下位のプランナーまで「表示された」として積まれ、
//    AgentSearchAnalysisService の search_offer_rate（分母がインプレッション数）が実態から外れる。
// 2. 認定プランナーの母集団は現状30件程度で、サーバの相性順（sort=compatibility）が
//    毎リクエストで行う全件スコアリングのコストは無視できる。per_page を大きくして
//    リクエスト回数を減らす動機が無い（1リクエストの採点コストは per_page に依存しない）。
//
// 母集団が数百件規模に増えたら、リクエスト回数と採点コストの釣り合いを見直すこと。
// ページ数の上限は設けない（上限を超えた順位のプランナーに到達できなくなるため）
export const AGENT_SEARCH_PER_PAGE = 20;

interface UseAgentSearchList {
  agents: Agent[];
  // 1ページ目の取得中（画面全体をローディング表示にする）
  isLoading: boolean;
  // 2ページ目以降の取得中（一覧は出したまま末尾にだけローディングを出す）
  isLoadingMore: boolean;
  error: boolean;
  // 追加読み込みだけが失敗している状態（取得済みの一覧は保持したまま末尾にエラーを出す）
  loadMoreError: boolean;
  // 直前の追加読み込みで一覧が1件も増えなかったか（ページ跨ぎで全件が既出だった等）。
  // これを手動導線へ切り替える条件にするかは画面側の判断なので、ここでは事実だけを返す
  lastLoadAddedNothing: boolean;
  hasMore: boolean;
  // 末尾監視（IntersectionObserver）による自動追加読み込みが使えるか。
  // false の環境では画面側が明示的な「続きを読み込む」ボタンを出す（出さないと2ページ目以降へ到達できない）
  isAutoLoadSupported: boolean;
  // サーバが返した母集団全体の件数（取得前・異常系は undefined）。PCの件数表示に使う
  total: number | undefined;
  // 条件を指定して1ページ目から取り直す
  fetchAgents: (tagIds: number[]) => Promise<void>;
  // 現在の条件のまま次ページを取得して末尾に追記する
  fetchMore: () => Promise<void>;
}

/**
 * プランナー検索一覧の取得。条件（検索タグ）の有無にかかわらず、
 * 検索フィードの match セクション単体 API（POST /v1/agents_feeds/{section}）一本で取得する。
 *
 * 3セクション束ね API（POST /v1/agents_feeds）は match / top_rated / active を同時に組み立て、
 * サーバが各件のマッチ度（AgentCompatibilityRadarScorer）を1件ずつ計算する。この画面は match しか
 * 使わないため、束ね API を使うと着地のたびに捨てる 2/3 の計算が発生する。母集団は同じ
 * （サーバの :match 分岐が display_in_match_feed: true の同じ取得に委譲している）ため、
 * section 単体に絞っても表示内容は変わらない。
 *
 * 相性順の一覧 API（GET /v1/agents?sort=compatibility）は母集団が
 * is_displayed_in_compatibility_feed（ホーム・登録フロー用の掲載フラグ）で、検索フィードの
 * is_displayed_in_match_feed とは別物。経路を分けると、運用が検索面で非表示にしたプランナーが
 * 条件なし着地にだけ出てしまう掲載フラグの二重管理が生まれるため、この画面の管轄は match_feed に一本化する。
 *
 * 並び順は sort=compatibility でサーバに任せる。サーバは絞り込み後の母集団「全体」をマッチ度順に
 * 並べてからページングするため、ページを跨いでも並びが崩れない。クライアント側では並べ替えず、
 * 返ってきた順序のまま描画する（useCompatibilityAgents と同じ方針）。
 *
 * 追加読み込みは AGENT_SEARCH_PER_PAGE 件単位。次があるかはレスポンスの total と取得済み件数の
 * 突き合わせで判定し、total が返らない・ページが空で返るといった異常系では
 * 同じページを取り続けないよう hasMore を false に倒す。
 *
 * 失敗の見え方に注意。サーバの get_agents_section_paginated_response は match セクション組み立て中の例外を
 * rescue で握り、200 + agents: [] を返す。そのためセクション内部の障害はこのフックの error ではなく
 * 「0件」として現れ、画面には「条件に一致するプランナーが見つかりませんでした」が出る。
 * 下の catch（error state）が拾えるのは通信失敗・認証失敗など、レスポンス自体が返らない/エラーになる経路だけ。
 */

// 追加読み込みを続けてよいかの判定。total が数値で返らない異常系と、
// ページが0件で返った異常系では、同じページを無限に取りに行かないよう止める（安全側）
const resolveHasMore = (total: number | undefined, fetchedCount: number, loadedCount: number): boolean => {
  if (fetchedCount === 0) return false;
  if (typeof total !== "number") return false;
  return loadedCount < total;
};

// 追記時の重複除去。サーバは毎リクエストでランキングを組み直すため、ページ取得の合間に
// プランナーが増減したり掲載フラグが変わると同じ agent が2ページに現れる。そのまま concat すると
// React の key 重複警告（CaptureConsole 経由で Sentry 行き）とカードの二重表示になる。
// id を持たない agent は同一性を判定できないため、除外せずそのまま残す（表示データを欠落させない）。
// 判定に使うキーは表示中の agents の id だけで、外部入力がキー空間に入ることはない
const appendWithoutDuplicates = (prev: Agent[], fetched: Agent[]): Agent[] => {
  const seenIds = new Set(prev.map((a) => a.id).filter((id): id is number => typeof id === "number"));
  const appended: Agent[] = [];
  fetched.forEach((agent) => {
    if (typeof agent.id === "number") {
      if (seenIds.has(agent.id)) return;
      seenIds.add(agent.id);
    }
    appended.push(agent);
  });
  return [...prev, ...appended];
};

export const useAgentSearchList = (): UseAgentSearchList => {
  const [agents, setAgents] = useState<Agent[]>([]);
  // 呼び出し側はマウント時に必ず fetchAgents を呼ぶが、その effect は初回ペイントの後に走る。
  // 初期値を false にすると「取得前」と「取得して0件」が区別できず、
  // 着地の1フレームだけ空状態（条件に一致するプランナーがいません）が誤表示される。
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [lastLoadAddedNothing, setLastLoadAddedNothing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | undefined>(undefined);

  // 検索とリセットは連打・往復されうるため、先発のレスポンスが後着して
  // 一覧と条件バーが食い違うのを防ぐ。世代は単調増加させるだけでリセットしない
  // （リセットすると過去の世代と衝突し、古い結果を最新と誤認するため）。
  const requestGenerationRef = useRef(0);
  // 取得中に画面を離れうるため、アンマウント後に応答が届いても setState しないようガードする
  const mountedRef = useRef(true);

  // 追加読み込みは「現在の世代の続き」を取る操作なので、条件・取得済みページ・取得済み件数を ref で持つ。
  // state 経由にすると fetchMore の同一性が変わり、末尾監視の IntersectionObserver を張り直す羽目になる
  const searchTagIdsRef = useRef<number[]>([]);
  // 0 は「1ページ目をまだ取れていない」を表す（取得前・取り直し中は追加読み込みを走らせない）
  const loadedPageRef = useRef(0);
  const loadedCountRef = useRef(0);
  const hasMoreRef = useRef(false);
  // disabled 相当の state はレンダーを跨いで反映されるため、末尾監視の連続発火に間に合わない。ref でも弾く
  const isLoadingMoreRef = useRef(false);
  // 追記の重複除去と「今回何件増えたか」の判定を setAgents の updater の外で行うための写し。
  // updater は純関数であることが前提（React が eager 評価や再実行をしうる）なので副作用を置かない
  const agentsRef = useRef<Agent[]>([]);

  const fetchAgents = useCallback(async (tagIds: number[]) => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    // 最新のリクエストかどうか。後発に追い越された結果は state に反映しない
    const isLatest = () => mountedRef.current && requestGenerationRef.current === generation;

    // 条件が変われば母集団もページ位置も別物になるため、追加読み込みの進捗はここで捨てる。
    // 世代も進んでいるので、進行中だった fetchMore のレスポンスは着いても無視される
    searchTagIdsRef.current = tagIds;
    agentsRef.current = [];
    loadedPageRef.current = 0;
    loadedCountRef.current = 0;
    hasMoreRef.current = false;
    isLoadingMoreRef.current = false;
    setHasMore(false);
    setIsLoadingMore(false);
    setLoadMoreError(false);
    setLastLoadAddedNothing(false);

    setIsLoading(true);
    setError(false);
    // 条件が変わると母集団も変わるため、前の条件の件数を出し続けない
    setTotal(undefined);
    try {
      const api = PropallyApiFactory();
      // 条件なしはタグを渡さない（絞り込みのない match セクションが返る）
      const response = await api.v1AgentsFeedsSectionPost(
        "match",
        1,
        AGENT_SEARCH_PER_PAGE,
        AgentsSortOptionEnum.Compatibility,
        tagIds.length === 0 ? {} : { company_agent_search_tag_ids: tagIds },
        { withCredentials: true }
      );
      if (!isLatest()) return;
      // section 単体のレスポンスは PaginatedAgentsResponse で agents が data 直下に来る
      // （3セクション束ねの data.match.agents とは形が違う）
      const fetchedAgents = response.data?.agents ?? [];
      loadedPageRef.current = 1;
      loadedCountRef.current = fetchedAgents.length;
      const nextHasMore = resolveHasMore(response.data?.total, fetchedAgents.length, fetchedAgents.length);
      hasMoreRef.current = nextHasMore;
      setHasMore(nextHasMore);
      setTotal(typeof response.data?.total === "number" ? response.data.total : undefined);
      // サーバが確定した順序のまま描画する（クライアント側で並び替えない）
      agentsRef.current = fetchedAgents;
      setAgents(fetchedAgents);
    } catch {
      // 呼び出し側で .catch されずに unhandled rejection になるのを避けるため、ここで握って error state に落とす。
      // Sentry へは custom_api_factory の interceptor が既に送っており、ここで console.error すると
      // CaptureConsole(levels: error) が同じ失敗を二重に拾うため、あえてログを出さない
      if (isLatest()) setError(true);
    } finally {
      // 追い越された世代が finally でローディングを解除すると、後発の取得中にスピナーが消えるため世代を確認する
      if (isLatest()) setIsLoading(false);
    }
  }, []);

  const fetchMore = useCallback(async () => {
    // 多重発火の防止。取得中・これ以上ない・1ページ目未取得のいずれでも何もしない
    if (isLoadingMoreRef.current || !hasMoreRef.current || loadedPageRef.current === 0) return;

    // 追加読み込みは開始時点の世代の続きを取る操作。着地までに条件が変わっていたら、
    // 別条件の結果が末尾に混ざらないよう破棄する
    const generation = requestGenerationRef.current;
    const isLatest = () => mountedRef.current && requestGenerationRef.current === generation;
    const nextPage = loadedPageRef.current + 1;
    const tagIds = searchTagIdsRef.current;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    setLoadMoreError(false);
    setLastLoadAddedNothing(false);
    try {
      const api = PropallyApiFactory();
      const response = await api.v1AgentsFeedsSectionPost(
        "match",
        nextPage,
        AGENT_SEARCH_PER_PAGE,
        AgentsSortOptionEnum.Compatibility,
        tagIds.length === 0 ? {} : { company_agent_search_tag_ids: tagIds },
        { withCredentials: true }
      );
      if (!isLatest()) return;
      const fetchedAgents = response.data?.agents ?? [];
      loadedPageRef.current = nextPage;
      // 加算はサーバが返した件数で行う（重複除去後の件数ではない）。loadedCountRef は
      // サーバの total と突き合わせて次があるかを見るための「サーバ基準の取得済み件数」であり、
      // 表示件数で数えると total と母数がずれて hasMore の判定が狂う（重複が出るほど終端に着けなくなる）
      loadedCountRef.current += fetchedAgents.length;
      const nextHasMore = resolveHasMore(response.data?.total, fetchedAgents.length, loadedCountRef.current);
      hasMoreRef.current = nextHasMore;
      setHasMore(nextHasMore);
      // 取得できたのに1件も増えなかった事実を返す（ページ跨ぎで全件が既出だった等）。
      // これをどう扱うかは画面側の判断。判定は setAgents の updater の外で行う
      // （updater は純関数であることが前提で、React が eager 評価や再実行をしうる）
      const before = agentsRef.current;
      const appended = appendWithoutDuplicates(before, fetchedAgents);
      agentsRef.current = appended;
      if (appended.length === before.length && nextHasMore) setLastLoadAddedNothing(true);
      setAgents(appended);
    } catch {
      // 追加取得の失敗で error を立てると、既に見えている一覧がエラー表示に置き換わって取得済みの分まで失う。
      // 取得済みは残したまま末尾にだけ失敗を出し、明示的な再試行で取り直せるようにする。
      // hasMore は落とさずページ位置も進めないので、再試行すると同じページを取り直す
      if (isLatest()) setLoadMoreError(true);
    } finally {
      // 世代が進んでいる場合は既に新しい取得が始まっている可能性があるため、そちらのフラグを消さない
      if (isLatest()) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    // effect が張り直されたときに false のまま残らないよう、本体で戻す
    // （React 17 かつ StrictMode 無しの現状は不要だが、Fast Refresh と将来の React 18 化への保険）
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // IntersectionObserver は本リポジトリで初採用のため、未実装の WebView がありうる。
  // 非対応だと末尾監視が張れず2ページ目以降へ到達する手段が無くなるので、画面側が
  // 「続きを読み込む」ボタンを出せるよう対応状況を公開する。
  // 判定は typeof のみ（外部入力は関与しない）で、実行中に結果が変わることもない
  const isAutoLoadSupported = typeof IntersectionObserver !== "undefined";

  return {
    agents,
    isLoading,
    total,
    isLoadingMore,
    error,
    loadMoreError,
    lastLoadAddedNothing,
    hasMore,
    isAutoLoadSupported,
    fetchAgents,
    fetchMore,
  };
};
